import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { fetchWikiRoutine, refreshWikiRoutine } from 'ducks/buildingWiki/actions'
import { selectWikiByBp } from 'ducks/buildingWiki/selectors'
import * as wikiService from 'services/BuildingWikiService'
import WikiSidebar from './components/WikiSidebar'
import WikiSection from './components/WikiSection'
import WikiReferences from './components/WikiReferences'
import WikiActionChip from './components/WikiActionChip'
import WikiAnomalyCallout from './components/WikiAnomalyCallout'
import { actionsForAnomaly, pageHeaderActions } from './actions'

const Page = styled.div`
  background: rgb(247, 248, 253);
  padding: 24px;
  border-radius: 8px;
  color: #333;
`

const Header = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 9px rgba(0, 0, 0, 0.03);
  padding: 24px 30px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
`

const HeaderLeft = styled.div`
  flex: 1;
`

const Title = styled.h1`
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #0c1229;
`

const Address = styled.div`
  font-size: 13px;
  color: #999;
  margin-bottom: 12px;
`

const Meta = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`

const PageActions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 10px;
`

const AnomalySpotlight = styled.section`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 9px rgba(0, 0, 0, 0.03);
  padding: 20px 24px;
  margin-bottom: 16px;
`

const AnomalySpotlightTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  color: #b91c1c;
  margin-bottom: 8px;
`

const StatsStrip = styled.section`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 9px rgba(0, 0, 0, 0.03);
  padding: 18px 22px;
  margin-bottom: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
`

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px;
  border-radius: 6px;
  background: ${({ tone }) => {
    if (tone === 'crit') return '#fef2f2'
    if (tone === 'warn') return '#fff7ed'
    if (tone === 'ok') return '#ecfdf5'
    return '#fcfdfe'
  }};
  border: 1px solid ${({ tone }) => {
    if (tone === 'crit') return '#fecaca'
    if (tone === 'warn') return '#fed7aa'
    if (tone === 'ok') return '#a7f3d0'
    return '#f2f2f2'
  }};
`

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 600;
  line-height: 1;
  color: ${({ tone }) => {
    if (tone === 'crit') return '#b91c1c'
    if (tone === 'warn') return '#c2410c'
    if (tone === 'ok') return '#065f46'
    return '#0c1229'
  }};
`

const StatLabel = styled.div`
  font-size: 11px;
  color: #666;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  font-weight: 500;
`

const HeaderChip = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  ${({ tone }) => {
    if (tone === 'crit') return 'background:#fef2f2;color:#b91c1c;border:1px solid #fecaca;'
    if (tone === 'warn') return 'background:#fff7ed;color:#c2410c;border:1px solid #fed7aa;'
    if (tone === 'ok') return 'background:#ecfdf5;color:#065f46;border:1px solid #a7f3d0;'
    return 'background:#f2f2f2;color:#333;'
  }}
`

const RefreshButton = styled.button`
  background: transparent;
  color: #d24724;
  border: 1px solid #d24724;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    color: #999;
    border-color: #cccccc;
    cursor: not-allowed;
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 16px;
`

const Article = styled.main`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 9px rgba(0, 0, 0, 0.03);
  padding: 30px;
`

const Banner = styled.div`
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  color: #7c2d12;
  font-size: 13px;
`

const Empty = styled.div`
  padding: 40px;
  text-align: center;
  color: #999;
`

const FirstRun = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 9px rgba(0, 0, 0, 0.03);
  padding: 56px 40px;
  margin-top: 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const FirstRunLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const FirstRunBadge = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  background: #fbede9;
  color: #d24724;
  padding: 4px 10px;
  border-radius: 9999px;
  margin-bottom: 16px;
`

const FirstRunHeading = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #0c1229;
  margin: 0 0 12px 0;
  line-height: 1.3;
`

const FirstRunCopy = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #555;
  margin: 0 0 20px 0;
`

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #fbede9;
  border-top-color: #d24724;
  border-radius: 50%;
  animation: wiki-spin 0.9s linear infinite;
  vertical-align: middle;
  margin-right: 8px;

  @keyframes wiki-spin {
    to { transform: rotate(360deg); }
  }
`

const StatusLine = styled.div`
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #555;
  margin-bottom: 8px;
`

const PreviewList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 18px;

  li {
    font-size: 13px;
    color: #555;
    padding-left: 22px;
    position: relative;
    line-height: 1.4;
  }
  li::before {
    content: '✓';
    color: #33becc;
    font-weight: 600;
    position: absolute;
    left: 0;
    top: 0;
  }
`

const FirstRunRight = styled.div`
  background: linear-gradient(135deg, #fcfdfe 0%, #fbede9 100%);
  border: 1px solid #f2f2f2;
  border-radius: 12px;
  padding: 28px;
`

const FirstRunRightTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  color: #999;
  margin-bottom: 12px;
`

const anomalyToneCounts = (anomalies = []) => {
  const counts = { crit: 0, warn: 0, missing: 0 }
  for (const a of anomalies) {
    if (a.severity === 'critical') counts.crit++
    else if (a.severity === 'high' || a.severity === 'medium') counts.warn++
    if (a.code === 'missing_compliance_type') counts.missing++
  }
  return counts
}

const WikiPage = ({ buildingPassport }) => {
  const dispatch = useDispatch()
  const bpId = buildingPassport?.id
  const hasTaskManagement = buildingPassport?.hasTaskManagement ?? buildingPassport?.has_task_management ?? false
  const wikiState = useSelector((state) => selectWikiByBp(state, bpId))
  const { article, status, refreshInFlight } = wikiState
  const [activeSectionKey, setActiveSectionKey] = useState(null)
  const [activeRefId, setActiveRefId] = useState(null)

  useEffect(() => {
    if (bpId) dispatch(fetchWikiRoutine({ bpId }))
  }, [bpId, dispatch])

  // Auto-kick generation the first time a user opens a brand-new wiki.
  // Guarded by a ref so a cooldown 429 doesn't make us loop.
  const autoKickedRef = useRef(null)
  useEffect(() => {
    if (!bpId || !article) return
    if (autoKickedRef.current === bpId) return
    if (refreshInFlight) return
    if (article.status === 'pending' && (article.sections?.length ?? 0) === 0) {
      autoKickedRef.current = bpId
      dispatch(refreshWikiRoutine({ bpId }))
    }
  }, [article?.status, article?.sections?.length, bpId, dispatch, refreshInFlight, article])

  useEffect(() => {
    if (!bpId) return
    const isWaiting = article?.status === 'generating' || article?.status === 'pending' || refreshInFlight
    if (!isWaiting) return
    const interval = setInterval(() => {
      dispatch(fetchWikiRoutine({ bpId }))
    }, 8000)
    return () => clearInterval(interval)
  }, [article?.status, bpId, dispatch, refreshInFlight])

  const onRefresh = useCallback(() => {
    dispatch(refreshWikiRoutine({ bpId }))
  }, [bpId, dispatch])

  const onSectionClick = useCallback((key) => {
    setActiveSectionKey(key)
    const el = document.getElementById(`section-${key}`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const onCitationActivate = useCallback((refId) => {
    setActiveRefId(refId)
    const el = document.getElementById(`ref-${refId}`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [])

  const [exporting, setExporting] = useState(false)
  const [spotlightExpanded, setSpotlightExpanded] = useState(false)
  const focusAnomalies = useCallback(() => {
    setSpotlightExpanded(true)
    const el = document.getElementById('section-anomaly-spotlight')
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])
  const onExportPdf = useCallback(async () => {
    if (!bpId || exporting) return
    setExporting(true)
    try {
      const { data, headers } = await wikiService.exportWikiPdf({ bpId })
      const disposition = headers?.['content-disposition'] || ''
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/i)
      const filename = filenameMatch?.[1] || 'building-overview.pdf'
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      const status = err?.response?.status
      if (status === 404) {
        toast.warning('Generate the wiki first, then try exporting again.')
      } else {
        toast.error('Could not export PDF.')
      }
    } finally {
      setExporting(false)
    }
  }, [bpId, exporting])

  if (!article && status === 'loading') {
    return <Empty>Loading wiki…</Empty>
  }

  const hiddenFromYou = article?.status === 'ready' && !!article?.error && (article.sections || []).length === 0

  if (hiddenFromYou) {
    return (
      <Page>
        <Header>
          <HeaderLeft>
            <Title>{buildingPassport?.name || 'Building'}</Title>
            <Address>
              {[buildingPassport?.street, buildingPassport?.city, buildingPassport?.postalCode]
                .filter(Boolean)
                .join(', ')}
            </Address>
          </HeaderLeft>
        </Header>
        <FirstRun>
          <FirstRunLeft>
            <FirstRunHeading>This overview is hidden for now</FirstRunHeading>
            <FirstRunCopy>{article.error}</FirstRunCopy>
            <FirstRunCopy>
              To ask for those documents, open the Documents tab and choose Request access. Each one is approved by
              the person who uploaded it or by their organisation.
            </FirstRunCopy>
          </FirstRunLeft>
        </FirstRun>
      </Page>
    )
  }

  const isFirstRun = !article || (
    (article.status === 'pending' || article.status === 'generating') &&
    (article.sections || []).length === 0
  )

  if (isFirstRun) {
    const generating = article?.status === 'generating' || refreshInFlight || article?.status === 'pending'
    return (
      <Page>
        <Header>
          <HeaderLeft>
            <Title>{buildingPassport?.name || 'Building'}</Title>
            <Address>
              {[buildingPassport?.street, buildingPassport?.city, buildingPassport?.postalCode]
                .filter(Boolean)
                .join(', ')}
            </Address>
          </HeaderLeft>
          <RefreshButton onClick={onRefresh} disabled={refreshInFlight || generating}>
            {generating ? 'Generating…' : 'Generate now'}
          </RefreshButton>
        </Header>

        <FirstRun>
          <FirstRunLeft>
            <FirstRunBadge>New</FirstRunBadge>
            <FirstRunHeading>
              {generating
                ? 'Preparing your wiki…'
                : 'Your building wiki is ready to be created'}
            </FirstRunHeading>
            <FirstRunCopy>
              The wiki gives you a Wikipedia-style overview of {buildingPassport?.name || 'this Building Passport'} —
              every fact pulled from the Building Passport&apos;s files, members, tasks, incidents, and notes,
              with every claim linked to its source.
            </FirstRunCopy>
            <StatusLine>
              {generating ? (
                <>
                  <Spinner />
                  Reading the Building Passport&apos;s records and writing the article. This usually takes 1–2 minutes.
                </>
              ) : (
                <>The first generation runs automatically — you can also trigger it now.</>
              )}
            </StatusLine>
          </FirstRunLeft>

          <FirstRunRight>
            <FirstRunRightTitle>What you&apos;ll see</FirstRunRightTitle>
            <PreviewList>
              <li>A plain-English overview of the building</li>
              <li>People, roles, and responsibilities</li>
              <li>Compliance state with overdue alerts</li>
              <li>Documents grouped by category</li>
              <li>Recent activity and notes</li>
              <li>Every fact cited to its source</li>
            </PreviewList>
          </FirstRunRight>
        </FirstRun>
      </Page>
    )
  }

  const sections = article.sections || []
  const references = article.references || []
  const anomalies = article.anomalies || []
  const stats = article.stats || {}
  const counts = anomalyToneCounts(anomalies)
  const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 }
  const sortedAnomalies = [...anomalies].sort(
    (a, b) => (severityOrder[a.severity] ?? 9) - (severityOrder[b.severity] ?? 9),
  )
  const topAnomalies = spotlightExpanded ? sortedAnomalies : sortedAnomalies.slice(0, 4)

  return (
    <Page>
      <Header>
        <HeaderLeft>
          <Title>{buildingPassport?.name || 'Building'}</Title>
          <Address>
            {[buildingPassport?.street, buildingPassport?.city, buildingPassport?.postalCode]
              .filter(Boolean)
              .join(', ')}
          </Address>
          <Meta>
            {buildingPassport?.occupied !== undefined && (
              <HeaderChip tone={buildingPassport.occupied ? 'ok' : ''}>
                {buildingPassport.occupied ? 'Occupied' : 'Vacant'}
              </HeaderChip>
            )}
            {counts.crit > 0 && (
              <HeaderChip tone="crit">{counts.crit} critical issue{counts.crit === 1 ? '' : 's'}</HeaderChip>
            )}
            {counts.warn > 0 && (
              <HeaderChip tone="warn">{counts.warn} concern{counts.warn === 1 ? '' : 's'}</HeaderChip>
            )}
            {anomalies.length === 0 && <HeaderChip tone="ok">No anomalies detected</HeaderChip>}
          </Meta>
          <PageActions>
            {pageHeaderActions(bpId).map((a, idx) => (
              <WikiActionChip key={idx} Icon={a.Icon} label={a.label} href={a.href} variant={a.variant} title={a.title} />
            ))}
            <WikiActionChip
              Icon={null}
              icon="⤓"
              label={exporting ? 'Preparing PDF…' : 'Export PDF'}
              onClick={onExportPdf}
              title="Download a branded PDF copy of this overview"
            />
          </PageActions>
        </HeaderLeft>
        <RefreshButton onClick={onRefresh} disabled={refreshInFlight}>
          {refreshInFlight ? 'Refreshing…' : 'Refresh wiki'}
        </RefreshButton>
      </Header>

      {Object.keys(stats).length > 0 && (
        <StatsStrip className="wiki-stats">
          {stats.critical > 0 && (
            <Stat
              tone="crit"
              onClick={focusAnomalies}
              role="button"
              style={{ cursor: 'pointer' }}
              title="Click to see every critical issue"
            >
              <StatValue tone="crit">{stats.critical}</StatValue>
              <StatLabel>Critical issue{stats.critical === 1 ? '' : 's'}</StatLabel>
            </Stat>
          )}
          {stats.concerns > 0 && (
            <Stat
              tone="warn"
              onClick={focusAnomalies}
              role="button"
              style={{ cursor: 'pointer' }}
              title="Click to see every concern"
            >
              <StatValue tone="warn">{stats.concerns}</StatValue>
              <StatLabel>Concerns</StatLabel>
            </Stat>
          )}
          {stats.complianceOverdue !== undefined && (
            <Stat tone={stats.complianceOverdue > 0 ? 'crit' : 'ok'}>
              <StatValue tone={stats.complianceOverdue > 0 ? 'crit' : 'ok'}>
                {stats.complianceOverdue}
                <span style={{ fontSize: 14, color: '#999', fontWeight: 400 }}>/{stats.complianceTotal ?? 0}</span>
              </StatValue>
              <StatLabel>Overdue docs</StatLabel>
            </Stat>
          )}
          {stats.forecast60d !== undefined && (
            <Stat tone={stats.forecast60d > 0 ? 'warn' : ''}>
              <StatValue tone={stats.forecast60d > 0 ? 'warn' : ''}>{stats.forecast60d}</StatValue>
              <StatLabel>Due in 60 days</StatLabel>
            </Stat>
          )}
          {stats.people !== undefined && (
            <Stat>
              <StatValue>{stats.people}</StatValue>
              <StatLabel>People</StatLabel>
            </Stat>
          )}
          {stats.units !== undefined && (
            <Stat>
              <StatValue>{stats.units}</StatValue>
              <StatLabel>Units</StatLabel>
            </Stat>
          )}
          {stats.documentsCurrent !== undefined && (
            <Stat>
              <StatValue>{stats.documentsCurrent > 999 ? `${(stats.documentsCurrent / 1000).toFixed(stats.documentsCurrent >= 10000 ? 0 : 1)}k` : stats.documentsCurrent}</StatValue>
              <StatLabel>Current docs</StatLabel>
              {stats.documentsTotal > stats.documentsCurrent && (
                <div style={{ fontSize: 10, color: '#999', marginTop: 2, textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
                  of {stats.documentsTotal.toLocaleString()} total uploads
                </div>
              )}
            </Stat>
          )}
          {hasTaskManagement && stats.actionItems !== undefined && (
            <Stat tone={stats.actionItems > 0 ? 'warn' : ''}>
              <StatValue tone={stats.actionItems > 0 ? 'warn' : ''}>{stats.actionItems}</StatValue>
              <StatLabel>Remedials</StatLabel>
            </Stat>
          )}
          {hasTaskManagement && stats.tasks !== undefined && (
            <Stat>
              <StatValue>{stats.tasks}</StatValue>
              <StatLabel>Open tasks</StatLabel>
            </Stat>
          )}
          {stats.incidents !== undefined && (
            <Stat tone={stats.incidents > 0 ? 'warn' : ''}>
              <StatValue tone={stats.incidents > 0 ? 'warn' : ''}>{stats.incidents}</StatValue>
              <StatLabel>Incidents</StatLabel>
            </Stat>
          )}
          {stats.reports !== undefined && stats.reports > 0 && (
            <Stat>
              <StatValue>{stats.reports}</StatValue>
              <StatLabel>Reports</StatLabel>
            </Stat>
          )}
        </StatsStrip>
      )}

      {sortedAnomalies.length > 0 && (
        <AnomalySpotlight id="section-anomaly-spotlight">
          <AnomalySpotlightTitle>
            Needs attention · {anomalies.length} issue{anomalies.length === 1 ? '' : 's'} detected
          </AnomalySpotlightTitle>
          {topAnomalies.map((a, idx) => (
            <WikiAnomalyCallout key={idx} anomaly={a} actions={actionsForAnomaly(a, bpId, hasTaskManagement)} />
          ))}
          {sortedAnomalies.length > 4 && (
            <button
              type="button"
              onClick={() => setSpotlightExpanded((v) => !v)}
              style={{
                background: 'transparent',
                border: '1px solid #fbede9',
                color: '#d24724',
                padding: '6px 12px',
                borderRadius: 9999,
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
                marginTop: 4,
              }}
            >
              {spotlightExpanded
                ? `Show top 4 only`
                : `Show all ${sortedAnomalies.length} issues`}
            </button>
          )}
        </AnomalySpotlight>
      )}

      {(status === 'regenerating' || article.status === 'generating') && (
        <Banner>Regenerating wiki — the latest content will appear shortly.</Banner>
      )}
      {article.status === 'needs_review' && (
        <Banner>This article has been flagged for review. Some claims could not be cited and were dropped.</Banner>
      )}
      {article.status === 'failed' && (
        <Banner>The last regeneration failed. The article below shows the previously generated content.</Banner>
      )}

      <Grid>
        <WikiSidebar
          sections={sections}
          references={references}
          activeSectionKey={activeSectionKey}
          generatedAt={article.generatedAt}
          onSectionClick={onSectionClick}
        />
        <Article>
          {sections.map((s) => (
            <WikiSection key={s.key} section={s} onCitationActivate={onCitationActivate} bpId={bpId} hasTaskManagement={hasTaskManagement} onRegenerate={onRefresh} regenerating={refreshInFlight} />
          ))}
          {references.length > 0 && <WikiReferences references={references} activeRefId={activeRefId} />}
        </Article>
      </Grid>
    </Page>
  )
}

export default WikiPage
